'use client';

import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Tab, TabGroup, type TabVariant } from './tabs';

export interface PageTabItem {
  value: string;
  label: ReactNode;
  /** Omit the tab entirely (e.g. behind a feature flag / permission). */
  hidden?: boolean;
  disabled?: boolean;
  /** Optional trailing count/badge. */
  badge?: ReactNode;
  /** Small status marker after the label (e.g. unsaved changes). */
  indicator?: 'dot';
  /** Accessible text for the indicator, e.g. "Unsaved changes". Required when `indicator` is set. */
  indicatorLabel?: string;
}

export interface PageTabGroup {
  id: string;
  /** Group heading (visible on md+, always the group's accessible name). */
  label: string;
  /** `PageTabItem.value`s in this group, in display order. */
  values: string[];
}

export interface PageTabsProps {
  tabs: PageTabItem[];
  variant?: TabVariant;
  /** Controlled active value. */
  value?: string;
  /** Uncontrolled initial value (ignored when `value` or `urlParam` is set). */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * Opt-in guard called with the candidate tab value before selection commits (on click, before
   * `onSelect`/URL write/`onValueChange`). Return `false` to bail — e.g. an active tab holds
   * unsaved edits and wants to show its own confirm affordance before the switch proceeds.
   * Default undefined = no interception (fully backward-compatible).
   */
  onBeforeSelect?: (next: string) => boolean;
  /**
   * When set, the active tab is read from and written to this URL query param
   * (e.g. "tab" → ?tab=). Absorbs the per-page useSearchParams + router.replace
   * boilerplate. Mutually exclusive with `value`.
   */
  urlParam?: string;
  /** Right-side header slot (e.g. action buttons), as in the *EditTabs pages. */
  actions?: ReactNode;
  /**
   * Content for the active tab. When set, PageTabs renders it below the bar
   * wrapped in a `role=tabpanel` labelled by the active tab — the one-component
   * form for pages with a single switched content area. Omit it (and render
   * your own `TabPanel`s) for multi-panel or structurally-separated layouts.
   */
  panel?: ReactNode;
  /** Accessible name for the tablist. */
  ariaLabel?: string;
  /** Shared id root for aria wiring; pass the SAME value to `TabPanel`. */
  idBase?: string;
  className?: string;
  /**
   * Primary variant only: render the tabs in labelled groups inside the one tablist. Tabs in no
   * group land in a trailing unlabelled group.
   */
  groups?: PageTabGroup[];
  /**
   * What happens when the primary bar is too narrow. `more` collapses the tabs that do
   * not fit into a "More" menu (the active tab is never collapsed); `scroll` (default) keeps the
   * horizontally scrolling, fading pill. Non-primary variants always scroll.
   */
  overflow?: 'scroll' | 'more';
}

/**
 * Canonical tabbed-page navigation. Owns the container chrome (semantic tokens,
 * no `dark:` pairs), the active-state wiring (controlled / uncontrolled / URL),
 * and accessibility (role=tablist/tab, aria-selected, roving tabIndex, Arrow/
 * Home/End keyboard nav). Built on the `Tab`/`TabGroup` visual recipes.
 *
 * Variant rule: `primary` = entity/page tabs, `subtle` = view toggles,
 * `underline` = sub-section/editorial. Use `TabPanel` for full panel a11y.
 */
export function PageTabs(props: PageTabsProps) {
  const reactId = useId();
  const idBase = props.idBase ?? `pt${reactId}`;
  return props.urlParam ? (
    <UrlPageTabs {...props} idBase={idBase} urlParam={props.urlParam} />
  ) : (
    <StatePageTabs {...props} idBase={idBase} />
  );
}

function firstVisible(tabs: PageTabItem[]): string {
  return tabs.find((t) => !t.hidden)?.value ?? '';
}

function UrlPageTabs({ urlParam, ...rest }: PageTabsProps & { urlParam: string; idBase: string }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Case-insensitive so a hand-typed ?tab=Players resolves to the canonical
  // 'players' value — and stays in sync with pages that lowercase the param.
  const raw = searchParams.get(urlParam);
  const matched = rest.tabs.find((t) => !t.hidden && t.value.toLowerCase() === raw?.toLowerCase());
  const active = matched ? matched.value : (rest.defaultValue ?? firstVisible(rest.tabs));

  const select = useCallback(
    (next: string) => {
      const sp = new URLSearchParams(searchParams.toString());
      sp.set(urlParam, next);
      router.replace(`${pathname}?${sp.toString()}`, { scroll: false });
      rest.onValueChange?.(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, router, pathname, urlParam, rest.onValueChange],
  );

  return <TabsBar {...rest} active={active} onSelect={select} />;
}

function StatePageTabs({ value, ...rest }: PageTabsProps & { idBase: string }) {
  const [internal, setInternal] = useState(() => rest.defaultValue ?? firstVisible(rest.tabs));
  const active = value !== undefined ? value : internal;

  const select = useCallback(
    (next: string) => {
      if (value === undefined) setInternal(next);
      rest.onValueChange?.(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, rest.onValueChange],
  );

  return <TabsBar {...rest} active={active} onSelect={select} />;
}

interface TabsBarProps extends Omit<PageTabsProps, 'value' | 'defaultValue' | 'urlParam'> {
  idBase: string;
  active: string;
  onSelect: (value: string) => void;
}

// Space reserved for the "More" trigger and the gap between tabs when computing what fits.
const MORE_TRIGGER_WIDTH = 88;
const TAB_GAP = 4;
const FALLBACK_TAB_WIDTH = 100;
const GROUP_GAP = 12;

function IndicatorDot({ label }: { label?: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      className="bg-warning ml-1.5 inline-block h-2 w-2 rounded-full align-middle"
    />
  );
}

function MoreMenu({
  hidden,
  onPick,
}: {
  hidden: PageTabItem[];
  /** Returns true when the selection committed (the guard, if any, allowed it). */
  onPick: (value: string) => boolean;
}) {
  const t = useTranslations('Common.buttons');
  const flagged = hidden.find((h) => h.indicator === 'dot');
  // The picked tab is committed once the menu has closed: its focus trap would otherwise pull focus
  // back out of a leave dialog the guard opens in the same commit, and drop it on <body>.
  const picked = useRef<string | null>(null);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Tab type="button" variant="primary" className="shrink-0 rounded-full">
          {t('more')}
          <ChevronDownIcon className="ml-1 inline h-4 w-4 align-middle" aria-hidden="true" />
          {flagged && <IndicatorDot label={flagged.indicatorLabel} />}
        </Tab>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          // After a pick, focus is placed deliberately (the new tab, or the leave dialog when the
          // guard blocked it) instead of going back to the More trigger. Escape still returns there.
          onCloseAutoFocus={(e) => {
            const value = picked.current;
            if (value === null) return;
            picked.current = null;
            e.preventDefault();
            onPick(value);
          }}
          className="bg-card border-border text-foreground z-50 min-w-[10rem] rounded-md border p-1 shadow-md"
        >
          {hidden.map((h) => (
            <DropdownMenu.Item
              key={h.value}
              disabled={h.disabled}
              onSelect={() => {
                picked.current = h.value;
              }}
              className="hover:bg-surface focus:bg-surface cursor-pointer rounded px-3 py-2 text-sm outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
            >
              {h.label}
              {h.indicator === 'dot' && <IndicatorDot label={h.indicatorLabel} />}
              {h.badge != null && (
                <span className="bg-foreground/10 ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold">
                  {h.badge}
                </span>
              )}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function TabsBar({
  tabs,
  variant = 'primary',
  actions,
  panel,
  ariaLabel,
  idBase,
  className,
  active,
  onSelect,
  onBeforeSelect,
  groups,
  overflow = 'scroll',
}: TabsBarProps) {
  const grouped = variant === 'primary' && !!groups?.length;
  const collapsible = variant === 'primary' && overflow === 'more';

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && groups?.length && variant !== 'primary') {
      console.warn('PageTabs: `groups` is only supported by the primary variant; ignoring it.');
    }
  }, [groups, variant]);

  // Group order is the display order (and the keyboard order). Tabs in no group trail.
  const { ordered, sections } = useMemo(() => {
    const live = tabs.filter((t) => !t.hidden);
    if (!grouped) {
      return { ordered: live, sections: [{ id: '', label: '', items: live }] };
    }
    const byValue = new Map(live.map((t) => [t.value, t]));
    const used = new Set<string>();
    const secs = groups!.map((g) => ({
      id: g.id,
      label: g.label,
      items: g.values.flatMap((v) => {
        const tab = byValue.get(v);
        if (!tab || used.has(v)) return [];
        used.add(v);
        return [tab];
      }),
    }));
    const rest = live.filter((t) => !used.has(t.value));
    if (rest.length) secs.push({ id: '__rest', label: '', items: rest });
    return { ordered: secs.flatMap((sec) => sec.items), sections: secs };
  }, [tabs, groups, grouped]);

  // --- overflow measurement ---------------------------------------------------------------
  // SSR / first render shows every tab. Once the ResizeObserver fires we know the bar width and
  // the tab widths (cached by value, since collapsed tabs leave the DOM) and collapse the rest.
  const barRef = useRef<HTMLDivElement | null>(null);
  const widths = useRef(new Map<string, number>());
  const measuredRef = useRef(false);
  const [fitCount, setFitCount] = useState<number | null>(null);
  const activeRef = useRef(active);
  const orderedRef = useRef(ordered);
  const groupCountRef = useRef(1);
  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const refList = useRef<Array<HTMLButtonElement | null>>([]);
  useLayoutEffect(() => {
    activeRef.current = active;
    orderedRef.current = ordered;
    groupCountRef.current = grouped ? sections.filter((sec) => sec.items.length > 0).length : 1;
  });

  const measure = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    const list = orderedRef.current;
    btnRefs.current.forEach((el, value) => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) widths.current.set(value, w);
    });
    const available = bar.getBoundingClientRect().width;
    if (available <= 0) {
      // not laid out (hidden / no layout engine): keep showing everything
      setFitCount(null);
      return;
    }
    const known = [...widths.current.values()];
    const avg = known.length ? known.reduce((a, b) => a + b, 0) / known.length : FALLBACK_TAB_WIDTH;
    const w = (v: string) => (widths.current.get(v) ?? avg) + TAB_GAP;
    const gaps = (groupCountRef.current - 1) * GROUP_GAP;
    const total = list.reduce((sum, t) => sum + w(t.value), gaps);
    if (total <= available) {
      setFitCount(null);
      return;
    }
    const activeIdx = list.findIndex((t) => t.value === activeRef.current);
    let n = 0;
    let used = MORE_TRIGGER_WIDTH + gaps;
    while (n < list.length && used + w(list[n].value) <= available) {
      used += w(list[n].value);
      n++;
    }
    // The active tab takes the last fitting slot when it would otherwise collapse.
    if (activeIdx >= n && n > 0) {
      while (n > 1 && used - w(list[n - 1].value) + w(list[activeIdx].value) > available) {
        used -= w(list[n - 1].value);
        n--;
      }
    }
    setFitCount(Math.max(n, 1));
  }, []);

  useLayoutEffect(() => {
    if (!collapsible) {
      measuredRef.current = false;
      setFitCount(null);
      return;
    }
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      measuredRef.current = true;
      measure();
    });
    ro.observe(bar);
    return () => ro.disconnect();
  }, [collapsible, measure]);

  // Width cache signature: anything that can change a tab's rendered width.
  const orderKey = ordered
    .map((t) => {
      const text = (n: ReactNode) =>
        typeof n === 'string' || typeof n === 'number' ? String(n) : '';
      return `${t.value}:${text(t.label)}:${text(t.badge)}:${t.indicator ?? ''}`;
    })
    .join('|');
  const sigRef = useRef(orderKey);
  useLayoutEffect(() => {
    if (sigRef.current !== orderKey) {
      sigRef.current = orderKey;
      widths.current.clear();
    }
    if (collapsible && measuredRef.current) measure();
  }, [collapsible, measure, orderKey, active]);

  const { visible, collapsed } = useMemo(() => {
    if (!collapsible || fitCount === null || fitCount >= ordered.length) {
      return { visible: ordered, collapsed: [] as PageTabItem[] };
    }
    const idx = ordered.findIndex((t) => t.value === active);
    const keep = ordered.slice(0, fitCount);
    if (idx >= fitCount) keep[fitCount - 1] = ordered[idx];
    const keepSet = new Set(keep.map((t) => t.value));
    return {
      visible: ordered.filter((t) => keepSet.has(t.value)),
      collapsed: ordered.filter((t) => !keepSet.has(t.value)),
    };
  }, [collapsible, fitCount, ordered, active]);

  // Manual activation: Arrow/Home/End move focus only; selection happens on
  // click / Enter / Space. This avoids mounting (and data-fetching) every
  // intermediate tab panel while arrowing across — the WAI-ARIA "tabs with
  // manual activation" pattern, recommended when panels are expensive.
  const focusTab = (index: number) => {
    const t = visible[index];
    if (!t || t.disabled) return;
    refList.current[index]?.focus();
  };

  const move = (from: number, dir: 1 | -1) => {
    const n = visible.length;
    if (n === 0) return;
    let i = from;
    for (let step = 0; step < n; step++) {
      i = (i + dir + n) % n;
      if (!visible[i]?.disabled) {
        focusTab(i);
        return;
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(index, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(index, -1);
        break;
      case 'Home':
        e.preventDefault();
        focusTab(visible.findIndex((t) => !t.disabled));
        break;
      case 'End':
        e.preventDefault();
        for (let i = visible.length - 1; i >= 0; i--) {
          if (!visible[i].disabled) {
            focusTab(i);
            break;
          }
        }
        break;
    }
  };

  // Roving tabindex: the active tab is the single tab stop. When the active
  // value matches no visible/enabled tab (unknown ?tab=, transient hidden
  // state), fall back to the first enabled tab so the list stays Tab-reachable.
  const activeIndex = visible.findIndex((t) => t.value === active && !t.disabled);
  const stopIndex = activeIndex >= 0 ? activeIndex : visible.findIndex((t) => !t.disabled);

  const commit = (value: string): boolean => {
    if (onBeforeSelect && !onBeforeSelect(value)) return false;
    onSelect(value);
    return true;
  };

  // A More-menu pick removes the menu item the user was on, so move focus to the tab it activated
  // once that tab is rendered (it may take the last visible slot, or arrive via a URL write).
  const pendingFocus = useRef<string | null>(null);
  useLayoutEffect(() => {
    const target = pendingFocus.current;
    if (target === null || target !== active) return;
    const el = btnRefs.current.get(target);
    if (el) {
      pendingFocus.current = null;
      el.focus();
    }
  });
  const pickFromMore = (value: string): boolean => {
    const committed = commit(value);
    if (committed) pendingFocus.current = value;
    return committed;
  };

  const renderTab = (t: PageTabItem, groupLabelId?: string) => {
    const i = visible.indexOf(t);
    const isActive = t.value === active;
    return (
      <Tab
        key={t.value}
        ref={(el) => {
          refList.current[i] = el;
          if (el) btnRefs.current.set(t.value, el);
          else btnRefs.current.delete(t.value);
        }}
        type="button"
        variant={variant}
        active={isActive}
        disabled={t.disabled}
        role="tab"
        id={`${idBase}-tab-${t.value}`}
        aria-selected={isActive}
        aria-describedby={groupLabelId}
        tabIndex={i === stopIndex ? 0 : -1}
        onClick={() => commit(t.value)}
        onKeyDown={(e) => onKeyDown(e, i)}
      >
        {t.label}
        {t.indicator === 'dot' && <IndicatorDot label={t.indicatorLabel} />}
        {t.badge != null && (
          <span className="bg-foreground/10 ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold">
            {t.badge}
          </span>
        )}
      </Tab>
    );
  };

  const pill = 'border-border inline-flex overflow-hidden rounded-full border text-sm';

  const tablist = grouped ? (
    <TabGroup
      variant={variant}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className="items-end gap-3 space-x-0"
    >
      {sections
        .map((sec) => ({ ...sec, items: sec.items.filter((t) => visible.includes(t)) }))
        .filter((sec) => sec.items.length > 0)
        .map((sec) => (
          <div key={sec.id} role="presentation" className="flex flex-col gap-1">
            {sec.label && (
              <span
                id={`${idBase}-grp-${sec.id}`}
                aria-hidden="true"
                className="text-muted sr-only px-2 text-[11px] font-semibold tracking-wide uppercase md:not-sr-only"
              >
                {sec.label}
              </span>
            )}
            <div className={cn(pill, 'space-x-1')}>
              {sec.items.map((t) =>
                renderTab(t, sec.label ? `${idBase}-grp-${sec.id}` : undefined),
              )}
            </div>
          </div>
        ))}
    </TabGroup>
  ) : (
    <TabGroup variant={variant} role="tablist" aria-label={ariaLabel} aria-orientation="horizontal">
      {visible.map((t) => renderTab(t))}
    </TabGroup>
  );

  // primary = the canonical rounded pill (semantic border-border, no dark: pair),
  // horizontally scrollable; subtle/underline carry their own track from TabGroup.
  // In `scroll` mode the right-edge mask fades the last tab to
  // hint "scroll for more"; in `more` mode overflowing tabs move into the More menu instead.
  const fade = 'linear-gradient(to right, black 0, black calc(100% - 24px), transparent 100%)';
  const maskActive = overflow === 'scroll';
  const more = collapsed.length > 0 && <MoreMenu hidden={collapsed} onPick={pickFromMore} />;
  const bar =
    variant === 'primary' ? (
      <div
        ref={barRef}
        data-slot="page-tabs-bar"
        className="overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]"
        style={maskActive ? { maskImage: fade, WebkitMaskImage: fade } : undefined}
      >
        {grouped ? (
          <div className="flex min-w-max items-end gap-2">
            {tablist}
            {more && <div className={cn(pill, 'self-end')}>{more}</div>}
          </div>
        ) : (
          <div className={cn(pill, 'min-w-max')}>
            {tablist}
            {more}
          </div>
        )}
      </div>
    ) : (
      tablist
    );

  const header = actions ? (
    <div
      className={cn(
        'flex flex-col gap-3 md:flex-row md:items-center md:justify-between',
        !panel && className,
      )}
    >
      <div className="w-full min-w-0 md:flex-1">{bar}</div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  ) : (
    <div className={panel ? undefined : className}>{bar}</div>
  );

  if (!panel) return header;

  // Label the panel by the tab that's actually rendered (the roving tab stop) so
  // aria-labelledby never dangles when `active` maps to a hidden/unknown tab.
  const labelledValue = stopIndex >= 0 ? visible[stopIndex].value : undefined;

  return (
    <div className={cn('space-y-6', className)}>
      {header}
      <div
        role="tabpanel"
        id={`${idBase}-panel-${active}`}
        aria-labelledby={labelledValue ? `${idBase}-tab-${labelledValue}` : undefined}
      >
        {panel}
      </div>
    </div>
  );
}

export interface TabPanelProps {
  value: string;
  /** The currently-active tab value. */
  active: string;
  /** Must match the `idBase` passed to the sibling `PageTabs`. */
  idBase: string;
  children: ReactNode;
  className?: string;
}

/**
 * Opt-in panel that wires role=tabpanel + aria-labelledby to its tab.
 *
 * No `tabIndex` is set: these panels hold their own focusable content (forms,
 * buttons), so a panel-level tab stop would be redundant. Panels with NO
 * focusable content should pass `tabIndex={0}` via a wrapper to stay reachable.
 */
export function TabPanel({ value, active, idBase, children, className }: TabPanelProps) {
  const isActive = value === active;
  return (
    <div
      role="tabpanel"
      id={`${idBase}-panel-${value}`}
      aria-labelledby={`${idBase}-tab-${value}`}
      hidden={!isActive}
      className={className}
    >
      {isActive ? children : null}
    </div>
  );
}
